// RedeemPage.jsx
// Route: /redeem/:slug
// QR 2 lands here — redemption flow only
// GPS + HMAC + time window all validated backend

import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

const C = {
  deepBlue:  "#1a3a5c",
  midBlue:   "#2563a8",
  lightBlue: "#dbeafe",
  cream:     "#fdf6e3",
  saffron:   "#f5a623",
  white:     "#ffffff",
  textDark:  "#0f2138",
  textMid:   "#4a6580",
  textLight: "#8faec8",
  success:   "#16a34a",
  error:     "#dc2626",
  border:    "#c8ddf0",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Playfair+Display:wght@700;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.cream}; font-family: 'Nunito', sans-serif; color: ${C.textDark}; min-height: 100vh; }

  .redeem-wrap {
    max-width: 420px; margin: 0 auto; min-height: 100vh;
    background: ${C.cream}; position: relative; overflow-x: hidden;
  }
  .redeem-wrap::before {
    content: ''; position: fixed; inset: 0;
    background-image: radial-gradient(circle, ${C.border} 1px, transparent 1px);
    background-size: 24px 24px; opacity: 0.4;
    pointer-events: none; z-index: 0;
  }
  .content { position: relative; z-index: 1; }

  .header {
    background: ${C.deepBlue}; padding: 20px 20px 28px;
    position: relative; overflow: hidden;
  }
  .header::after {
    content: ''; position: absolute; bottom: -16px; left: 0; right: 0;
    height: 32px; background: ${C.cream};
    border-radius: 50% 50% 0 0 / 100% 100% 0 0;
  }
  .header-brand { display: flex; align-items: center; gap: 10px; }
  .header-logo {
    width: 36px; height: 36px; background: ${C.white};
    border-radius: 50%; display: flex; align-items: center;
    justify-content: center; font-size: 18px;
  }
  .header-name { font-size: 18px; font-weight: 800; color: ${C.white}; }
  .header-sub { font-size: 12px; color: ${C.lightBlue}; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; }

  /* ── States ── */
  .state-wrap {
    padding: 40px 20px;
    display: flex; flex-direction: column; align-items: center;
    text-align: center; gap: 12px;
  }
  .state-icon { font-size: 52px; }
  .state-title {
    font-family: 'Playfair Display', serif;
    font-size: 22px; font-weight: 700; color: ${C.deepBlue};
  }
  .state-desc { font-size: 14px; color: ${C.textMid}; font-weight: 600; line-height: 1.5; max-width: 280px; }

  /* ── Balance pill ── */
  .balance-pill {
    background: ${C.deepBlue}; color: ${C.white};
    border-radius: 30px; padding: 10px 24px;
    display: inline-flex; align-items: baseline; gap: 6px;
    margin-bottom: 8px;
  }
  .balance-num {
    font-family: 'Playfair Display', serif;
    font-size: 36px; font-weight: 900; color: ${C.white};
  }
  .balance-label { font-size: 12px; font-weight: 700; color: ${C.lightBlue}; letter-spacing: 1px; }

  /* ── Reward selection ── */
  .rewards-list { width: 100%; display: flex; flex-direction: column; gap: 10px; margin-top: 8px; }
  .reward-item {
    background: ${C.white}; border-radius: 14px; padding: 16px 18px;
    border: 2px solid ${C.border}; display: flex; align-items: center; gap: 14px;
    cursor: pointer; transition: all 0.15s; text-align: left;
    width: 100%;
  }
  .reward-item.eligible { border-color: ${C.saffron}; }
  .reward-item.eligible:hover { background: #fffbf0; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(245,166,35,0.15); }
  .reward-item.locked { opacity: 0.5; cursor: not-allowed; }
  .reward-icon { font-size: 28px; flex-shrink: 0; }
  .reward-info { flex: 1; }
  .reward-name { font-size: 14px; font-weight: 800; color: ${C.textDark}; }
  .reward-eligibility { font-size: 12px; font-weight: 600; color: ${C.textMid}; margin-top: 2px; }
  .reward-pts { font-size: 15px; font-weight: 900; color: ${C.saffron}; }

  /* ── Confirm screen ── */
  .confirm-card {
    background: ${C.white}; border-radius: 20px; padding: 28px 24px;
    border: 1.5px solid ${C.border}; box-shadow: 0 8px 32px rgba(26,58,92,0.1);
    text-align: center; width: 100%;
  }
  .confirm-icon { font-size: 56px; margin-bottom: 8px; }
  .confirm-title {
    font-family: 'Playfair Display', serif;
    font-size: 26px; font-weight: 900; color: ${C.success}; margin-bottom: 6px;
  }
  .confirm-reward { font-size: 18px; font-weight: 800; color: ${C.deepBlue}; margin-bottom: 16px; }
  .confirm-code-box {
    background: ${C.cream}; border: 2px dashed ${C.border};
    border-radius: 12px; padding: 16px;
    margin-bottom: 16px;
  }
  .confirm-code-label { font-size: 11px; font-weight: 700; color: ${C.textMid}; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 6px; }
  .confirm-code {
    font-family: 'Playfair Display', serif;
    font-size: 28px; font-weight: 700; color: ${C.deepBlue}; letter-spacing: 3px;
  }
  .confirm-instruction {
    background: ${C.lightBlue}; border-radius: 10px; padding: 12px 16px;
    font-size: 13px; font-weight: 700; color: ${C.deepBlue}; line-height: 1.4;
  }
  .confirm-balance { font-size: 13px; color: ${C.textMid}; font-weight: 600; margin-top: 12px; }

  /* ── Buttons ── */
  .primary-btn {
    width: 100%; padding: 14px; background: ${C.deepBlue}; color: ${C.white};
    border: none; border-radius: 12px; font-size: 15px; font-weight: 800;
    font-family: 'Nunito', sans-serif; cursor: pointer;
    transition: background 0.2s, transform 0.1s;
  }
  .primary-btn:hover { background: ${C.midBlue}; }
  .primary-btn:active { transform: scale(0.98); }
  .primary-btn:disabled { background: ${C.border}; cursor: not-allowed; }
  .ghost-btn {
    background: none; border: 2px solid ${C.border}; border-radius: 12px;
    padding: 11px; font-size: 14px; font-weight: 700; color: ${C.textMid};
    font-family: 'Nunito', sans-serif; cursor: pointer; width: 100%;
    margin-top: 10px; transition: border-color 0.2s;
  }
  .ghost-btn:hover { border-color: ${C.midBlue}; color: ${C.midBlue}; }

  .loader { width: 36px; height: 36px; border: 3px solid ${C.border}; border-top-color: ${C.midBlue}; border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  .bottom-pad { height: 40px; }
`;

function rewardEmoji(name = "") {
  if (name.toLowerCase().includes("curd")) return "🥛";
  if (name.toLowerCase().includes("paneer")) return "🧀";
  if (name.toLowerCase().includes("milk")) return "🍼";
  if (name.toLowerCase().includes("lassi")) return "🥤";
  if (name.toLowerCase().includes("family")) return "👨‍👩‍👧‍👦";
  return "🎁";
}

export default function RedeemPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const sig = searchParams.get("sig") || "";

  const [phase, setPhase] = useState("loading");
  // loading | no_member | validated | confirming | confirmed | error | window_closed
  const [validateData, setValidateData] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedReward, setSelectedReward] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [gpsCoords, setGpsCoords] = useState(null);

  const BIDS = { milkas: "biz-a63374ba-df71-48d5-b419-3447eedd6c8f" };
  const businessId = BIDS[slug] || null;

  useEffect(() => {
    if (!businessId) { setPhase("error"); setErrorMsg("Shop not found."); return; }
    const saved = localStorage.getItem(`milkas_member_${businessId}`);
    if (!saved) { setPhase("no_member"); return; }
    const member = JSON.parse(saved);
    // Get GPS first then validate
    if (!navigator.geolocation) {
      setPhase("error"); setErrorMsg("Location not supported on this device."); return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { gpsLat: pos.coords.latitude, gpsLng: pos.coords.longitude };
        setGpsCoords(coords);
        doValidate(member.phone, coords);
      },
      () => { setPhase("error"); setErrorMsg("Please allow location access to redeem rewards."); },
      { timeout: 10000, maximumAge: 30000 }
    );
  }, [businessId]);

  async function doValidate(phone, coords) {
    try {
      const r = await fetch(`${API}/rewards/redeem/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, phone, sig, ...coords }),
      });
      const d = await r.json();
      if (r.ok) {
        setValidateData(d);
        setPhase("validated");
      } else {
        // Check if it's a time window error
        if (d.windowStart !== undefined) {
          setErrorMsg(d.error);
          setPhase("window_closed");
        } else {
          setErrorMsg(d.error || "Validation failed");
          setPhase("error");
        }
      }
    } catch {
      setPhase("error");
      setErrorMsg("Network error. Please try again.");
    }
  }

  async function handleConfirm() {
    if (!selectedReward || !gpsCoords) return;
    const saved = localStorage.getItem(`milkas_member_${businessId}`);
    if (!saved) { setPhase("no_member"); return; }
    const member = JSON.parse(saved);
    setConfirming(true);
    try {
      const r = await fetch(`${API}/rewards/redeem/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId,
          phone: member.phone,
          sig,
          rewardId: selectedReward.rewardId,
          ...gpsCoords,
        }),
      });
      const d = await r.json();
      if (r.ok) {
        // Update local member balance
        const updatedMember = { ...member, points: d.newBalance };
        localStorage.setItem(`milkas_member_${businessId}`, JSON.stringify(updatedMember));
        setConfirmData(d);
        setPhase("confirmed");
      } else {
        setErrorMsg(d.error || "Redemption failed. Please try again.");
        setPhase("error");
      }
    } catch {
      setErrorMsg("Network error. Please try again.");
      setPhase("error");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div className="redeem-wrap">
        <div className="content">

          <div className="header">
            <div className="header-brand">
              <div className="header-logo">🥛</div>
              <div>
                <div className="header-name">Milkas Dairy</div>
                <div className="header-sub">Redeem Rewards</div>
              </div>
            </div>
          </div>

          {/* ── LOADING ── */}
          {phase === "loading" && (
            <div className="state-wrap">
              <div className="loader" />
              <div className="state-desc">Verifying your location…</div>
            </div>
          )}

          {/* ── NO MEMBER ── */}
          {phase === "no_member" && (
            <div className="state-wrap">
              <div className="state-icon">🎁</div>
              <div className="state-title">Join First</div>
              <div className="state-desc">
                You need to join Milkas Rewards before redeeming.
              </div>
              <a href={`/rewards/${slug}`} style={{ textDecoration: "none", width: "100%", maxWidth: 300 }}>
                <button className="primary-btn">Join Milkas Rewards →</button>
              </a>
            </div>
          )}

          {/* ── WINDOW CLOSED ── */}
          {phase === "window_closed" && (
            <div className="state-wrap">
              <div className="state-icon">🕙</div>
              <div className="state-title">Come Back Later</div>
              <div className="state-desc">{errorMsg}</div>
              <div style={{
                background: C.lightBlue, borderRadius: 12, padding: "14px 20px",
                fontSize: 14, fontWeight: 700, color: C.deepBlue, marginTop: 8
              }}>
                Redemption hours:<br/>
                <span style={{ fontSize: 18, fontFamily: "'Playfair Display', serif" }}>10:00 AM – 4:00 PM</span>
              </div>
              <a href={`/rewards/${slug}`} style={{ textDecoration: "none", width: "100%", maxWidth: 300 }}>
                <button className="ghost-btn">← Back to My Rewards</button>
              </a>
            </div>
          )}

          {/* ── ERROR ── */}
          {phase === "error" && (
            <div className="state-wrap">
              <div className="state-icon">😕</div>
              <div className="state-title">Cannot Redeem</div>
              <div className="state-desc">{errorMsg}</div>
              <a href={`/rewards/${slug}`} style={{ textDecoration: "none", width: "100%", maxWidth: 300 }}>
                <button className="ghost-btn">← Back to My Rewards</button>
              </a>
            </div>
          )}

          {/* ── VALIDATED — choose reward ── */}
          {phase === "validated" && validateData && (
            <div style={{ padding: "32px 20px" }}>
              {/* Balance */}
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textMid, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
                  Welcome, {validateData.member?.name}
                </div>
                <div className="balance-pill">
                  <div className="balance-num">{validateData.member?.points}</div>
                  <div className="balance-label">POINTS</div>
                </div>
                <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600 }}>
                  Valid until {validateData.validUntil}
                </div>
              </div>

              {/* Eligible rewards */}
              {validateData.eligibleRewards?.length > 0 ? (
                <>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.textMid, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>
                    Choose Your Reward
                  </div>
                  <div className="rewards-list">
                    {validateData.eligibleRewards.map(r => (
                      <button
                        key={r.rewardId}
                        className={`reward-item eligible ${selectedReward?.rewardId === r.rewardId ? "selected" : ""}`}
                        onClick={() => setSelectedReward(r)}
                        style={selectedReward?.rewardId === r.rewardId ? {
                          background: "#fffbf0",
                          borderColor: C.saffron,
                          boxShadow: `0 0 0 3px ${C.saffronDim}`,
                        } : {}}
                      >
                        <div className="reward-icon">{rewardEmoji(r.name)}</div>
                        <div className="reward-info">
                          <div className="reward-name">{r.name}</div>
                          <div className="reward-eligibility" style={{ color: C.success }}>✅ You can redeem this</div>
                        </div>
                        <div className="reward-pts">{r.points} pts</div>
                      </button>
                    ))}

                    {/* Ineligible rewards */}
                    {validateData.ineligibleRewards?.map(r => (
                      <div key={r.rewardId} className="reward-item locked">
                        <div className="reward-icon">{rewardEmoji(r.name)}</div>
                        <div className="reward-info">
                          <div className="reward-name">{r.name}</div>
                          <div className="reward-eligibility">Need {r.pointsNeeded} more points</div>
                        </div>
                        <div className="reward-pts" style={{ color: C.textLight }}>{r.points} pts</div>
                      </div>
                    ))}
                  </div>

                  {selectedReward && (
                    <div style={{ marginTop: 20 }}>
                      <button
                        className="primary-btn"
                        onClick={handleConfirm}
                        disabled={confirming}
                      >
                        {confirming ? "Confirming…" : `Redeem ${selectedReward.name} →`}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="state-wrap" style={{ padding: "20px 0" }}>
                  <div className="state-icon">😔</div>
                  <div className="state-title">Not Enough Points</div>
                  <div className="state-desc">
                    Keep visiting Milkas to earn more points!
                  </div>
                  {validateData.ineligibleRewards?.map(r => (
                    <div key={r.rewardId} className="reward-item locked" style={{ width: "100%" }}>
                      <div className="reward-icon">{rewardEmoji(r.name)}</div>
                      <div className="reward-info">
                        <div className="reward-name">{r.name}</div>
                        <div className="reward-eligibility">Need {r.pointsNeeded} more points</div>
                      </div>
                      <div className="reward-pts" style={{ color: C.textLight }}>{r.points} pts</div>
                    </div>
                  ))}
                  <a href={`/rewards/${slug}`} style={{ textDecoration: "none", width: "100%" }}>
                    <button className="ghost-btn">← Back to My Rewards</button>
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ── CONFIRMED ── */}
          {phase === "confirmed" && confirmData && (
            <div style={{ padding: "32px 20px" }}>
              <div className="confirm-card">
                <div className="confirm-icon">🎉</div>
                <div className="confirm-title">Redeemed!</div>
                <div className="confirm-reward">{confirmData.rewardName}</div>

                <div className="confirm-code-box">
                  <div className="confirm-code-label">Confirmation Code</div>
                  <div className="confirm-code">{confirmData.confirmationCode}</div>
                </div>

                <div className="confirm-instruction">
                  📢 Show this screen to collect your {confirmData.rewardName} from the counter
                </div>

                <div className="confirm-balance">
                  Remaining balance: <strong>{confirmData.newBalance} points</strong>
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <a href={`/rewards/${slug}`} style={{ textDecoration: "none" }}>
                  <button className="ghost-btn">← Back to My Rewards</button>
                </a>
              </div>
            </div>
          )}

          <div className="bottom-pad" />
        </div>
      </div>
    </>
  );
}
